import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox } from 'antd';
import type { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { FilterDropdownProps } from 'antd/lib/table/interface';
import {
  buildOptionsFromData,
  DateRangeFilter,
  ErrorHandler,
  FilteredTable,
  Gap,
  Gutter,
  MultiSelectTableFilter,
  NumberRangeFilter,
  Text,
} from '@common/components';
import { useLazyQuery, usePaginationHelper, useQuery } from '@common/hooks';
import {
  fromISODateFormat,
  toHumanTimeFormat,
  toISODateFormat,
  toUSFormat,
} from 'works/legacy/lib/date-helper';
import { datetime, dayjsToMoment, parseZone } from '@common/services/datetime';
import { RoleType, RolesQuery } from '@clinician-profile/graphql';
import { OrganizationUnitType } from '@institutions/graphql';
import { ShiftTypeWithIcon } from '@jobs/components';
import { SHIFT_GAP_QUERY } from '@scheduling/graphql/queries/shift-gaps';
import { SHIFT_OPENINGS_FILTER_UNITS } from '@scheduling/graphql/queries/shift-openings';
import {
  InputMaybe,
  OrderDirection,
  ShiftGapFilterInput,
  ShiftGapOrderField,
} from '@common/graphql/schema';
import { BulkShiftStepSelectShiftsPanel } from './_bulk-shift-step-select-shifts-panel';
import {
  applyRangeSelection,
  applyTogglePage,
  removeGap,
  toGapEntry,
} from './_bulk-shift-step-select-shifts-service';
import type {
  BulkShiftGapRowType,
  SelectedGapEntryType,
} from './_bulk-shift-step-types';

const PAGE_SIZE = 100;
const FILTERS_LOCAL_STORAGE_KEY = 'BulkShiftIncentiveOverrideStep1';

// Bulk overrides only target shifts that have not started yet, so the date
// filter's lower bound is meaningless before today. ISO `YYYY-MM-DD` strings
// sort lexicographically, so a plain string comparison is enough — no need
// to round-trip through moment for the comparison itself.
function clampStartFilterToToday(
  start: ShiftGapFilterInput['start']
): ShiftGapFilterInput['start'] {
  if (!Array.isArray(start) || start.length !== 2 || !start[0]) return start;

  const today = toISODateFormat(datetime());
  if (start[0] >= today) return start;

  return [today, start[1]];
}

export function BulkShiftStepSelectShifts({
  selectedGaps,
  onSelectionChange,
}: {
  selectedGaps: SelectedGapEntryType[];
  onSelectionChange: (next: SelectedGapEntryType[]) => void;
}) {
  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: FILTERS_LOCAL_STORAGE_KEY,
    defaultPaginationData: {
      defaultPageSize: PAGE_SIZE,
      limit: PAGE_SIZE,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'start',
    },
    columnsDictionary: {
      start: 'start',
      duration: 'duration',
      role: 'role',
    },
    savePaginationInLocalStorage: false,
  });

  const [
    fetchOrganizationUnits,
    { data: organizationUnitData, error: organizationUnitError },
  ] = useLazyQuery(SHIFT_OPENINGS_FILTER_UNITS, { fetchPolicy: 'cache-first' });

  const [fetchRoles, { data: rolesData, error: rolesError }] =
    useLazyQuery(RolesQuery);

  // The backend service is the load-bearing guard against bulk-overriding
  // started shifts; this just keeps Step 1's table consistent with what the
  // apply will accept by hiding past dates from the query.
  const queryFilters = useMemo<ShiftGapFilterInput>(
    () => ({
      ...paginationVariables.filters,
      start: clampStartFilterToToday(paginationVariables.filters?.start),
    }),
    [paginationVariables.filters]
  );

  const { loading, error, data } = useQuery(SHIFT_GAP_QUERY, {
    variables: {
      ...paginationVariables,
      filters: queryFilters,
      orderDirection: paginationVariables.orderDirection as
        | InputMaybe<OrderDirection>
        | undefined,
      orderField: paginationVariables.orderField as
        | InputMaybe<ShiftGapOrderField>
        | undefined,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      fetchOrganizationUnits();
      fetchRoles();
    },
  });

  // Tracks the last-clicked gap on the current page so Shift+click can extend
  // the selection across the rows in between.
  const [lastClickedGapId, setLastClickedGapId] = useState<string | null>(null);

  const pageData = data?.paginatedShiftGaps;
  const shiftGaps = useMemo<BulkShiftGapRowType[]>(
    () => pageData?.shiftGaps ?? [],
    [pageData?.shiftGaps]
  );
  const total = pageData?.totalCount ?? null;

  const selectedGapIdSet = useMemo(
    () => new Set(selectedGaps.map((entry) => entry.shiftGapId)),
    [selectedGaps]
  );

  const allOnPageSelected =
    shiftGaps.length > 0 &&
    shiftGaps.every((gap) => selectedGapIdSet.has(gap.id));
  const someOnPageSelected =
    !allOnPageSelected && shiftGaps.some((gap) => selectedGapIdSet.has(gap.id));

  // Reset the Shift+click anchor when paging or filtering navigates away from
  // the row that was last clicked.
  useEffect(() => {
    if (
      lastClickedGapId &&
      !shiftGaps.some((gap) => gap.id === lastClickedGapId)
    ) {
      setLastClickedGapId(null);
    }
  }, [lastClickedGapId, shiftGaps]);

  const filtersDataReady = !!organizationUnitData && !!rolesData;

  const anyError = error || organizationUnitError || rolesError;
  if (anyError) {
    return <ErrorHandler error={anyError} log="BulkShiftSelectShiftsStep" />;
  }

  const handleClearAll = () => {
    onSelectionChange([]);
    setLastClickedGapId(null);
  };

  const handleRemove = (shiftGapId: string) => {
    onSelectionChange(removeGap(selectedGaps, shiftGapId));
    if (lastClickedGapId === shiftGapId) {
      setLastClickedGapId(null);
    }
  };

  // Custom checkbox column instead of AntD's built-in `rowSelection`. AntD's
  // selection wiring intercepts the checkbox click before our handler runs,
  // and the first Shift+click would land before `shiftKey` was readable on the
  // event — forcing users to click twice. Owning the checkbox lets us read
  // `e.nativeEvent.shiftKey` synchronously and drive range selection on the
  // first click.
  const handleRowCheckboxChange =
    (record: BulkShiftGapRowType) => (event: CheckboxChangeEvent) => {
      const selected = event.target.checked;
      const isShiftClick = event.nativeEvent.shiftKey === true;
      const targetGapId = record.id;

      if (
        isShiftClick &&
        lastClickedGapId &&
        lastClickedGapId !== targetGapId
      ) {
        onSelectionChange(
          applyRangeSelection({
            current: selectedGaps,
            pageGaps: shiftGaps,
            fromGapId: lastClickedGapId,
            toGapId: targetGapId,
            selected,
          })
        );
      } else if (selected) {
        const next = [...selectedGaps];
        const exists = next.some((entry) => entry.shiftGapId === targetGapId);
        if (!exists) next.push(toGapEntry(record));
        onSelectionChange(next);
      } else {
        onSelectionChange(removeGap(selectedGaps, targetGapId));
      }

      setLastClickedGapId(targetGapId);
    };

  const handleHeaderCheckboxChange = (event: CheckboxChangeEvent) => {
    onSelectionChange(
      applyTogglePage({
        current: selectedGaps,
        pageGaps: shiftGaps,
        selected: event.target.checked,
      })
    );
    setLastClickedGapId(null);
  };

  const columns = [
    {
      key: 'selection',
      width: 48,
      title: (
        <Checkbox
          checked={allOnPageSelected}
          indeterminate={someOnPageSelected}
          onChange={handleHeaderCheckboxChange}
        />
      ),
      render: (_: never, record: BulkShiftGapRowType) => (
        <Checkbox
          checked={selectedGapIdSet.has(record.id)}
          onChange={handleRowCheckboxChange(record)}
        />
      ),
    },
    {
      title: 'Start time',
      key: 'start',
      sorter: true,
      render: (_: never, record: BulkShiftGapRowType) => {
        const startTime = parseZone(record.startDatetime).tz(record.timeZone);
        return (
          <Gap vertical size="xs">
            <Text>{toUSFormat(startTime)}</Text>
            <Text bold>{toHumanTimeFormat(startTime)}</Text>
          </Gap>
        );
      },
      filterFormat: 'dateRange',
      filtered: !!filters.start,
      filteredValue: filters.start || null,
      filterDropdown: ({ setSelectedKeys, confirm }: FilterDropdownProps) => (
        <Gutter size="sm" asPadding>
          <DateRangeFilter
            defaultValue={filters.start?.map((date) =>
              // @TODO(WORK-6543): drop this bridge when scheduling (incentive-rate)
              // cuts over. fromISODateFormat returns a Dayjs post-M5.1 cutover, but
              // the antd v4 DateRangeFilter still needs a real Moment.
              dayjsToMoment(fromISODateFormat(date))
            )}
            autoFocus={true}
            onSearch={(value) => {
              setSelectedKeys(
                value ? value.map((date) => toISODateFormat(date)) : []
              );
              confirm();
            }}
          />
        </Gutter>
      ),
    },
    {
      title: 'Length',
      key: 'duration',
      sorter: true,
      render: (_: never, record: BulkShiftGapRowType) =>
        `${record.shiftDuration} hrs`,
      filterFormat: 'hoursRange',
      filtered: !!filters.duration,
      filteredValue: filters.duration || null,
      filterDropdown: ({ setSelectedKeys, confirm }: FilterDropdownProps) => (
        <Gutter size="sm" asPadding>
          <NumberRangeFilter
            onSearch={(value) => {
              setSelectedKeys(value || []);
              confirm();
            }}
            min={1}
            max={24}
            defaultValue={filters.duration?.map((value) => Number(value))}
            enforceOrder
          />
        </Gutter>
      ),
    },
    {
      title: 'Period',
      key: 'timeOfDay',
      filtered: !!filters.timeOfDay,
      filteredValue: filters.timeOfDay || null,
      filters: [
        { text: 'Day', value: 'day' },
        { text: 'Night', value: 'night' },
      ],
      filterDropdown: MultiSelectTableFilter,
      render: (_: never, record: BulkShiftGapRowType) => (
        <ShiftTypeWithIcon value={record.timeOfDay ?? undefined} />
      ),
    },

    {
      title: 'Unit / Cost Center',
      key: 'unit',
      filtered: !!filters.unit,
      filteredValue: filters.unit || null,
      filters: buildOptionsFromData(
        organizationUnitData?.organizationUnits || [],
        (unit: OrganizationUnitType) => ({
          text: `${unit.name} - ${unit.costCenter}`,
          value: unit.id,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      render: (_: never, record: BulkShiftGapRowType) => (
        <Gap vertical size="xs">
          <Text>{record.organizationUnit?.name}</Text>
          <Text secondary>{record.organizationUnit?.costCenter}</Text>
        </Gap>
      ),
    },
    {
      title: 'Role',
      key: 'role',
      sorter: true,
      filtered: !!filters.role,
      filteredValue: filters.role || null,
      filters: buildOptionsFromData(
        rolesData?.roles || [],
        (role: RoleType) => ({
          text: role.abbreviation,
          value: role.id,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      render: (_: never, record: BulkShiftGapRowType) => (
        <>{record.role?.abbreviation ?? '--'}</>
      ),
    },
  ];

  return (
    <Gap size="md" fullWidth vertical align="stretch">
      <Text>
        Hold <Text bold>Shift</Text> and click a checkbox to select a range.
        Your selections stay selected across pages.
      </Text>
      <BulkShiftStepSelectShiftsPanel
        selectedGaps={selectedGaps}
        onClearAll={handleClearAll}
        onRemove={handleRemove}
      />
      <FilteredTable
        rowKey="id"
        className="bulk-shift-select-step-table"
        columns={columns}
        loading={loading}
        filtersDataReady={filtersDataReady}
        dataSource={shiftGaps}
        pagination={{ ...paginationVariables, total }}
        onChange={tableChangeHandler}
        onFilterChange={filterChangeHandler}
        localStorageFilterKey={localStorageFilterKey}
        filters={filters}
      />
    </Gap>
  );
}
